import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';
import { api } from '../services/api';
import { getCheckoutPlatform, isNativeApp } from '../native-bridge';
import NativeBridge from '../native-bridge';
import Toast from '../components/common/Toast';

export const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const { t } = useTranslation();
  const {
    user: auth0User,
    isAuthenticated,
    isLoading: auth0Loading,
    loginWithRedirect,
    logout: auth0Logout,
    getAccessTokenSilently
  } = useAuth0();

  const [token, setToken] = useState(null);
  const [userStats, setUserStats] = useState({ generated: 0, quota: 10, remaining: 10 });
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  const showToastMessage = (message) => {
    setToastMessage(message);
    setShowToast(true);
  };

  // Get Auth0 token when user is authenticated
  useEffect(() => {
    const getToken = async () => {
      if (isAuthenticated) {
        try {
          const accessToken = await getAccessTokenSilently({
            authorizationParams: {
              audience: process.env.REACT_APP_AUTH0_AUDIENCE,
            }
          });
          setToken(accessToken);
        } catch (error) {
          console.error('Error getting access token:', error);
          // The refresh token is expired/invalid (e.g. past its absolute
          // lifetime), so Auth0 rejects the silent token request. Without
          // recovery the app is stuck half-authenticated: a stale cached user
          // makes `isAuthenticated` true while `token` stays null. Clear the
          // broken Auth0 session locally so the app falls back to the login
          // screen and the user can re-authenticate. (Native iOS handles its
          // own token lifecycle, so leave that path untouched.)
          const code = error?.error;
          const isInvalidGrant =
            code === 'invalid_grant' ||
            code === 'login_required' ||
            code === 'missing_refresh_token' ||
            /refresh token/i.test(error?.message || '');
          if (isInvalidGrant && !isNativeApp()) {
            setToken(null);
            auth0Logout({ openUrl: false });
          }
        }
      } else {
        setToken(null);
      }
    };
    getToken();
  }, [isAuthenticated, getAccessTokenSilently, auth0Logout]);

  // Handle native auth tokens injected by the iOS WebView
  useEffect(() => {
    if (!isNativeApp()) {
      return undefined;
    }

    const handleNativeToken = (event) => {
      const nativeToken = event?.detail?.token;
      if (nativeToken) {
        setToken(nativeToken);
        localStorage.setItem('nativeAuthToken', nativeToken);
      }
    };

    const handleNativeLogout = () => {
      setToken(null);
      localStorage.removeItem('nativeAuthToken');
      localStorage.removeItem('auth0.is.authenticated');
    };

    window.addEventListener('nativeAuthTokenReceived', handleNativeToken);
    window.addEventListener('nativeLogout', handleNativeLogout);

    // Check for stored token and validate it
    const initializeAuth = async () => {
      const storedToken = localStorage.getItem('nativeAuthToken');
      if (storedToken) {
        const isValid = await api.validateToken(storedToken);
        if (isValid) {
          setToken(storedToken);
        } else {
          console.log('[Auth] Stored token is invalid, clearing...');
          localStorage.removeItem('nativeAuthToken');
          setToken(null);
        }
      } else {
        NativeBridge.getAuthToken().then(async (nativeToken) => {
          if (nativeToken) {
            const isValid = await api.validateToken(nativeToken);
            if (isValid) {
              setToken(nativeToken);
              localStorage.setItem('nativeAuthToken', nativeToken);
            }
          }
        });
      }
    };

    initializeAuth();

    return () => {
      window.removeEventListener('nativeAuthTokenReceived', handleNativeToken);
      window.removeEventListener('nativeLogout', handleNativeLogout);
    };
  }, []);

  const fetchUserStats = async () => {
    if (!token) return;
    try {
      const stats = await api.getUserStats(token);
      setUserStats(stats);
    } catch (error) {
      console.error('Failed to fetch user stats:', error);
    }
  };

  useEffect(() => {
    if (token) {
      fetchUserStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const login = () => {
    if (window.webkit?.messageHandlers?.requestLogin) {
      window.webkit.messageHandlers.requestLogin.postMessage({});
      return;
    }
    if (window.location.protocol === 'file:') {
      console.error('Native bridge not available, aborting Auth0 web login.');
      return;
    }
    loginWithRedirect();
  };

  const logout = () => {
    setToken(null);
    localStorage.removeItem('nativeAuthToken');
    if (window.webkit?.messageHandlers?.requestLogout) {
      window.webkit.messageHandlers.requestLogout.postMessage({});
    } else {
      auth0Logout({ logoutParams: { returnTo: window.location.origin } });
    }
  };

  /**
   * Permanently delete the account, then sign out.
   *
   * Resolves to { ok, error } rather than throwing, so the confirmation dialog
   * can keep the user in place and show a reason when deletion fails -- being
   * dropped to a login screen with no explanation would be worse.
   */
  const deleteAccount = async () => {
    if (!token) {
      return { ok: false, error: t('auth.notAuthenticated') };
    }

    try {
      const { ok, data } = await api.deleteAccount(token);
      if (!ok) {
        return { ok: false, error: data?.error || t('account.deleteFailed') };
      }
    } catch (error) {
      console.error('Account deletion failed:', error);
      return { ok: false, error: t('account.deleteFailed') };
    }

    logout();
    return { ok: true };
  };

  const handleBuyCredits = async () => {
    try {
      const platform = getCheckoutPlatform();
      const isNative = platform === 'ios-app';
      const baseUrl = isNative ? 'https://mymanifest.app' : window.location.origin;
      const successPath = platform === 'ios-app'
        ? '/success-ios?session_id={CHECKOUT_SESSION_ID}'
        : platform === 'mobile-web'
          ? '/success-mobile?session_id={CHECKOUT_SESSION_ID}'
          : '/success-web?session_id={CHECKOUT_SESSION_ID}';
      const cancelPath = isNative ? '/app-redirect?target=home' : '/';
      const successUrl = `${baseUrl}${successPath}`;
      const cancelUrl = `${baseUrl}${cancelPath}`;

      const { url } = await api.createCheckoutSession(token, {
        type: 'credits',
        successUrl,
        cancelUrl,
        platform
      });

      if (isNativeApp() && window.webkit?.messageHandlers?.openExternalURL) {
        window.webkit.messageHandlers.openExternalURL.postMessage(url);
      } else {
        window.location.href = url;
      }
    } catch (error) {
      console.error('Payment error:', error);
      showToastMessage(t('payment.failedToInitiate'));
    }
  };

  const handleOrderPrint = async (boardId, type, shippingAddress = null) => {
    try {
      const platform = getCheckoutPlatform();
      const isNative = platform === 'ios-app';
      const baseUrl = isNative ? 'https://mymanifest.app' : window.location.origin;
      const successPath = platform === 'ios-app'
        ? '/success-ios?session_id={CHECKOUT_SESSION_ID}'
        : platform === 'mobile-web'
          ? '/success-mobile?session_id={CHECKOUT_SESSION_ID}'
          : '/success-web?session_id={CHECKOUT_SESSION_ID}';
      const cancelPath = isNative
        ? `/app-redirect?target=board&id=${boardId}`
        : `/board/${boardId}`;
      const successUrl = `${baseUrl}${successPath}`;
      const cancelUrl = `${baseUrl}${cancelPath}`;

      const { url } = await api.createCheckoutSession(token, {
        type,
        successUrl,
        cancelUrl,
        boardId,
        shippingAddress,
        platform
      });

      if (isNativeApp() && window.webkit?.messageHandlers?.openExternalURL) {
        window.webkit.messageHandlers.openExternalURL.postMessage(url);
      } else {
        window.location.href = url;
      }
    } catch (error) {
      console.error('Payment error:', error);
      showToastMessage(t('payment.failedToInitiate'));
    }
  };

  const verifyPayment = async (sessionId) => {
    if (!token) {
      return { success: false, error: t('auth.notAuthenticated') };
    }

    try {
      const { ok, data } = await api.verifyPaymentSession(token, sessionId);
      if (!ok) {
        return { success: false, error: data.error || t('payment.verificationFailed') };
      }
      await fetchUserStats();
      return { success: true, data };
    } catch (error) {
      console.error('Payment verification error:', error);
      return { success: false, error: error.message };
    }
  };

  const shareBoard = async (boardId) => {
    if (!token) {
      return { success: false, error: t('auth.notAuthenticated') };
    }

    try {
      const { ok, data } = await api.shareBoard(token, boardId);
      if (ok) {
        return { success: true, data };
      } else {
        return { success: false, error: data.error || t('share.shareFailed') };
      }
    } catch (error) {
      console.error('Share board error:', error);
      return { success: false, error: error.message };
    }
  };

  const unshareBoard = async (boardId) => {
    if (!token) {
      return { success: false, error: t('auth.notAuthenticated') };
    }

    try {
      const { ok, data } = await api.unshareBoard(token, boardId);
      if (ok) {
        return { success: true, data };
      } else {
        return { success: false, error: data.error || t('share.unshareFailed') };
      }
    } catch (error) {
      console.error('Unshare board error:', error);
      return { success: false, error: error.message };
    }
  };

  return (
    <AuthContext.Provider value={{
      user: auth0User,
      token,
      login,
      logout,
      isLoading: auth0Loading,
      isAuthenticated,
      userStats,
      fetchUserStats,
      deleteAccount,
      handleBuyCredits,
      handleOrderPrint,
      verifyPayment,
      shareBoard,
      unshareBoard
    }}>
      {showToast && <Toast message={toastMessage} onClose={() => setShowToast(false)} />}
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook for using auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
